// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import { Form, Flex, Typography, notification } from "antd";
import { useSelector } from "react-redux";
// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------
import { selectSelectedWorkout } from "store/selectors";
// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";
import { LiveIcon } from "components/Icons";
import alpha from "helpers/alpha";
import { CopyOutlined } from "@ant-design/icons";
// -----------------------------------------------------------------------------
// Utils and Styles
// -----------------------------------------------------------------------------
import StyledReleaseScheduleForm from "./styles";
import DateTimeTimezone from "components/DateTimeTimezone";
import { useGetDomainGymQuery } from "store/api/api";
import { useParams } from "react-router";
import { defaultTimezone, workoutTypes } from "helpers/constants";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const ReleaseScheduleForm = ({ nextStep, previousStep, values, preRecordedLive }) => {
	const [form] = Form.useForm();
	const selectedWorkout = useSelector(selectSelectedWorkout);
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const [currentDatetime, setCurrentDatetime] = useState(null);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
	const primaryColor = usePrimaryColor();

	const getPayload = () => {
		const values = form.getFieldsValue();
		const { workoutDate, workoutTime, workoutTimezone } = values;
		if (!workoutDate || !workoutTime || !workoutTimezone) {
			return {
				gym_id: domainGymId,
				timezone: workoutTimezone,
				currently_streaming: false,
			};
		}
		const day = dayjs(workoutDate).startOf("day").format("MM/DD/YYYY");
		const dayTime = dayjs(day + " " + workoutTime, "MM/DD/YYYY HH:mm");
		const iso = dayTime.format("YYYY-MM-DDTHH:mm:ss");
		return {
			gym_id: domainGymId,
			workout_id: selectedWorkout?.id,
			start_time: iso,
			timezone: workoutTimezone,
			time_zone: workoutTimezone,
			currently_streaming: false,
			workoutTime,
			type: workoutTypes.SCHEDULED_LIVE_STREAM,
		};
	};
	const onFinish = async () => {
		setIsSubmitDisabled(true);
		const payload = getPayload();
		nextStep(payload);
	};

	const onPreviousStep = () => {
		setIsSubmitDisabled(false);
		const payload = getPayload();
		previousStep(payload);
	};

	return (
		<StyledReleaseScheduleForm primaryColor={primaryColor}>
			<div className="sb-release-schedule-form">
				<Form
					data-id="live-details-form"
					layout="vertical"
					onFinish={onFinish}
					form={form}
					initialValues={{
						workoutDate: values.start_time ? dayjs(values.start_time) : undefined,
						workoutTime: values.workoutTime,
						workoutTimezone: values.timezone || defaultTimezone,
					}}
				>
					{!preRecordedLive && (
						<>
							<Typography.Text style={{ color: "#000" }}>Broadcast Server</Typography.Text>
							<Flex justify="space-between" className="stream-settings">
								<Typography.Text style={{ color: "#000" }}>rtmp://global-live.mux.com:5222/app</Typography.Text>
								<Typography.Text
									copyable={{
										text: "rtmp://global-live.mux.com:5222/app",
										icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
										onCopy: () => notification.success({ message: "Copied to clipboard" }),
									}}
									style={{
										color: primaryColor,
										backgroundColor: alpha(primaryColor, 0.1),
										borderRadius: "14px",
										padding: "4px 8px",
									}}
								>
									Copy
								</Typography.Text>
							</Flex>
						</>
					)}
					<div className="sb-form-date-timezone">
						<DateTimeTimezone form={form} currentDatetime={currentDatetime} setCurrentDatetime={setCurrentDatetime} />
					</div>

					{!preRecordedLive && (
						<div className="sb-release-schedule-form__notice" data-id="lblScheduleNote">
							<LiveIcon fill={primaryColor} />
							Live workouts require you to log in and start the workout at the selected time and date.
						</div>
					)}

					<Flex align="center" justify="center" className="sb-workout-music-form__actions">
						<Button type="button" onClick={onPreviousStep} className="cancel-btn" data-id="btnBack">
							Previous
						</Button>
						<Button
							type="submit"
							disabled={isSubmitDisabled}
							className="sb-release-schedule-form__form__button primary-filled"
							style={{ margin: 0 }}
							data-id="btnSubmit"
						>
							Next
						</Button>
					</Flex>
				</Form>
			</div>
		</StyledReleaseScheduleForm>
	);
};

export default ReleaseScheduleForm;

